import { useState, useEffect, useRef, useCallback } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Alert } from "@/components/ui/alert";
import { useAudioCapture } from "@/hooks/useAudioCapture";
import { useSpeakerWebSocket } from "@/hooks/useSpeakerWebSocket";
import {
  TranscriptionMessage,
  generateRoomId,
} from "@speech-translator/shared";
import {
  Copy,
  Mic,
  MicOff,
  Users,
  Share2,
  QrCode,
  AlertCircle,
  CheckCircle,
  X,
} from "lucide-react";
import { QRCodeModal } from "./QRCodeModal";

interface SpeakerRoomProps {
  roomId?: string;
  onRoomCreated?: (roomId: string) => void;
}

interface TranscriptionSegment {
  id: string;
  text: string;
  timestamp: number;
  isFinal: boolean;
  confidence?: number;
}

interface Notification {
  id: string;
  message: string;
  type: "info" | "success" | "warning" | "error";
  timestamp: number;
}

export function SpeakerRoom({
  roomId: initialRoomId,
  onRoomCreated,
}: SpeakerRoomProps) {
  // State management
  const [roomId, setRoomId] = useState<string>(initialRoomId || "");
  const [transcriptionSegments, setTranscriptionSegments] = useState<
    TranscriptionSegment[]
  >([]);
  const [isCreatingRoom, setIsCreatingRoom] = useState(false);
  const [showQRCode, setShowQRCode] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [previousParticipantCount, setPreviousParticipantCount] = useState(0);

  // Refs
  const transcriptionContainerRef = useRef<HTMLDivElement | null>(null);

  // WebSocket hook
  const webSocket = useSpeakerWebSocket({
    onTranscription: handleTranscription,
    onError: (message) => {
      console.error("WebSocket error:", message.data);
    },
  });

  // Audio capture hook
  const audioCapture = useAudioCapture({
    onAudioData: (audioData, sequence) => {
      console.log(
        `Audio data received in SpeakerRoom: sequence ${sequence}, ready: ${webSocket.isReadyForAudio()}`
      );
      if (webSocket.isReadyForAudio()) {
        webSocket.sendAudioData(audioData, sequence);
      } else {
        console.warn("WebSocket not ready for audio data");
      }
    },
    onError: (error) => {
      console.error("Audio capture error:", error);
    },
  });

  // Create or join room
  const handleCreateRoom = useCallback(async () => {
    setIsCreatingRoom(true);
    try {
      const newRoomId = generateRoomId();
      setRoomId(newRoomId);
      onRoomCreated?.(newRoomId);
      await webSocket.connect(newRoomId);
    } catch (error) {
      console.error("Failed to create room:", error);
    } finally {
      setIsCreatingRoom(false);
    }
  }, [onRoomCreated, webSocket]);

  // Handle transcription messages
  function handleTranscription(message: TranscriptionMessage) {
    const segment: TranscriptionSegment = {
      id: `${Date.now()}-${Math.random()}`,
      text: message.data.text,
      timestamp: message.timestamp,
      isFinal: message.data.isFinal,
      confidence: message.data.confidence,
    };

    setTranscriptionSegments((prev) => {
      // If this is a final transcription, replace any non-final segments
      if (segment.isFinal) {
        const filtered = prev.filter((s) => s.isFinal);
        return [...filtered, segment];
      } else {
        // Replace the last non-final segment if it exists
        const finalSegments = prev.filter((s) => s.isFinal);
        return [...finalSegments, segment];
      }
    });

    // Auto-scroll to bottom
    setTimeout(() => {
      if (transcriptionContainerRef.current) {
        transcriptionContainerRef.current.scrollTop =
          transcriptionContainerRef.current.scrollHeight;
      }
    }, 100);
  }

  // Toggle recording
  const toggleRecording = useCallback(async () => {
    console.log(
      "toggleRecording called, hasPermission:",
      audioCapture.hasPermission,
      "isReadyForAudio:",
      webSocket.isReadyForAudio()
    );

    if (!audioCapture.hasPermission) {
      console.log("No permission, initializing...");
      try {
        await audioCapture.initialize();
        console.log(
          "Initialization completed, hasPermission now:",
          audioCapture.hasPermission
        );
        
        // If initialization was successful, start recording immediately
        // Don't check hasPermission state - just call startRecording directly
        if (webSocket.isReadyForAudio()) {
          console.log("Starting recording after successful initialization");
          // Force start recording without state check
          setTimeout(() => {
            audioCapture.startRecording();
          }, 50);
        } else {
          console.log("WebSocket not ready after initialization");
        }
      } catch (error) {
        console.error("Failed to initialize audio:", error);
      }
      return;
    }

    if (!webSocket.isReadyForAudio()) {
      console.log("WebSocket not ready, clearing error...");
      webSocket.clearError();
      return;
    }

    console.log("Calling audioCapture.toggleRecording()");
    audioCapture.toggleRecording();
  }, [audioCapture, webSocket]);

  // Copy room link to clipboard
  const copyRoomLink = useCallback(async () => {
    const link = `${window.location.origin}/listener/${roomId}`;
    try {
      await navigator.clipboard.writeText(link);
      // Could add a toast notification here
    } catch (err) {
      console.error("Failed to copy link:", err);
    }
  }, [roomId]);

  // Auto-connect if roomId is provided
  useEffect(() => {
    if (roomId && webSocket.connectionStatus === "disconnected") {
      webSocket.connect(roomId);
    }
  }, [roomId, webSocket]);

  // Add notification helper
  const addNotification = useCallback(
    (message: string, type: Notification["type"]) => {
      const notification: Notification = {
        id: `${Date.now()}-${Math.random()}`,
        message,
        type,
        timestamp: Date.now(),
      };

      setNotifications((prev) => [...prev, notification]);

      // Auto-remove after 5 seconds
      setTimeout(() => {
        setNotifications((prev) =>
          prev.filter((n) => n.id !== notification.id)
        );
      }, 5000);
    },
    []
  );

  // Handle participant count changes for notifications
  useEffect(() => {
    if (webSocket.participantCount !== previousParticipantCount) {
      if (previousParticipantCount > 0) {
        // Don't show notification on initial load
        const difference =
          webSocket.participantCount - previousParticipantCount;
        if (difference > 0) {
          addNotification(
            `${difference} listener${difference > 1 ? "s" : ""} joined`,
            "success"
          );
        } else if (difference < 0) {
          addNotification(
            `${Math.abs(difference)} listener${
              Math.abs(difference) > 1 ? "s" : ""
            } left`,
            "info"
          );
        }
      }
      setPreviousParticipantCount(webSocket.participantCount);
    }
  }, [webSocket.participantCount, previousParticipantCount, addNotification]);

  // Remove notification manually
  const removeNotification = useCallback((id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  const getConnectionStatusBadge = () => {
    const statusConfig = {
      connecting: {
        variant: "secondary" as const,
        text: "Connecting...",
        icon: AlertCircle,
        className: "animate-pulse",
      },
      connected: {
        variant: "default" as const,
        text: "Connected",
        icon: CheckCircle,
        className: "bg-green-500 hover:bg-green-600",
      },
      disconnected: {
        variant: "destructive" as const,
        text: "Disconnected",
        icon: AlertCircle,
        className: "",
      },
      error: {
        variant: "destructive" as const,
        text: "Error",
        icon: AlertCircle,
        className: "animate-pulse",
      },
      reconnecting: {
        variant: "secondary" as const,
        text: "Reconnecting...",
        icon: AlertCircle,
        className: "animate-pulse",
      },
    };

    const config = statusConfig[webSocket.connectionStatus];
    const Icon = config.icon;
    return (
      <Badge
        variant={config.variant}
        className={`flex items-center gap-1 ${config.className}`}
      >
        <Icon className="h-3 w-3" />
        {config.text}
      </Badge>
    );
  };

  const getRecordingStatusBadge = () => {
    if (!audioCapture.hasPermission) {
      return <Badge variant="outline">No Microphone Access</Badge>;
    }
    if (audioCapture.isRecording) {
      return (
        <Badge variant="default" className="bg-red-500">
          Recording
        </Badge>
      );
    }
    return <Badge variant="outline">Ready</Badge>;
  };

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold mb-2">Speaker Room</h1>
        {roomId && (
          <p className="text-muted-foreground">
            Room ID: <span className="font-mono font-semibold">{roomId}</span>
          </p>
        )}
      </div>

      {/* Connection and Status */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Room Status</span>
            <div className="flex items-center gap-2">
              {getConnectionStatusBadge()}
              {getRecordingStatusBadge()}
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              <span>{webSocket.participantCount} listeners connected</span>
            </div>
            {roomId && (
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={copyRoomLink}>
                  <Copy className="h-4 w-4 mr-2" />
                  Copy Link
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowQRCode(true)}
                >
                  <QrCode className="h-4 w-4 mr-2" />
                  QR Code
                </Button>
              </div>
            )}
          </div>

          {!roomId && (
            <Button
              onClick={handleCreateRoom}
              className="w-full"
              disabled={isCreatingRoom}
            >
              <Share2 className="h-4 w-4 mr-2" />
              {isCreatingRoom ? "Creating Room..." : "Create Room"}
            </Button>
          )}

          {webSocket.error && (
            <div className="mt-4 p-3 bg-destructive/10 border border-destructive/20 rounded-md">
              <p className="text-sm text-destructive">{webSocket.error}</p>
              <Button
                variant="outline"
                size="sm"
                className="mt-2"
                onClick={() => webSocket.clearError()}
              >
                Dismiss
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Audio Controls */}
      {roomId && (
        <Card>
          <CardHeader>
            <CardTitle>Audio Controls</CardTitle>
            <CardDescription>
              {audioCapture.hasPermission
                ? "Click the microphone button to start/stop recording"
                : "Grant microphone permission to start speaking"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Microphone Button */}
              <div className="flex justify-center">
                <Button
                  size="lg"
                  variant={audioCapture.isRecording ? "destructive" : "default"}
                  onClick={toggleRecording}
                  disabled={webSocket.connectionStatus !== "connected"}
                  className="h-16 w-16 rounded-full"
                >
                  {audioCapture.isRecording ? (
                    <MicOff className="h-8 w-8" />
                  ) : (
                    <Mic className="h-8 w-8" />
                  )}
                </Button>
              </div>

              {/* Audio Level Visualization */}
              {audioCapture.hasPermission && (
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Audio Level</span>
                    <span>{Math.round(audioCapture.audioLevel)}%</span>
                  </div>
                  <Progress value={audioCapture.audioLevel} className="h-2" />
                </div>
              )}

              {/* Permission Error */}
              {audioCapture.permissionError && (
                <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md">
                  <p className="text-sm text-destructive">
                    {audioCapture.permissionError}
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-2"
                    onClick={audioCapture.initialize}
                  >
                    Try Again
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Real-time Transcription */}
      {roomId && (
        <Card>
          <CardHeader>
            <CardTitle>Live Transcription</CardTitle>
            <CardDescription>
              Your speech will appear here in real-time
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div
              ref={transcriptionContainerRef}
              className="h-64 overflow-y-auto p-4 bg-muted/50 rounded-md border"
            >
              {transcriptionSegments.length === 0 ? (
                <p className="text-muted-foreground text-center">
                  Start speaking to see transcription...
                </p>
              ) : (
                <div className="space-y-2">
                  {transcriptionSegments.map((segment) => (
                    <div
                      key={segment.id}
                      className={`p-2 rounded ${
                        segment.isFinal
                          ? "bg-background border"
                          : "bg-muted/30 italic"
                      }`}
                    >
                      <p className="text-sm">
                        {segment.text}
                        {segment.confidence && (
                          <span className="ml-2 text-xs text-muted-foreground">
                            ({Math.round(segment.confidence * 100)}%)
                          </span>
                        )}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {new Date(segment.timestamp).toLocaleTimeString()}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Notifications */}
      {notifications.length > 0 && (
        <div className="fixed top-4 right-4 z-40 space-y-2 max-w-sm">
          {notifications.map((notification) => (
            <Alert
              key={notification.id}
              variant={
                notification.type === "error" ? "destructive" : "default"
              }
              className="animate-in slide-in-from-right-full duration-300"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {notification.type === "success" && (
                    <CheckCircle className="h-4 w-4 text-green-500" />
                  )}
                  {notification.type === "info" && (
                    <AlertCircle className="h-4 w-4 text-blue-500" />
                  )}
                  {notification.type === "warning" && (
                    <AlertCircle className="h-4 w-4 text-yellow-500" />
                  )}
                  {notification.type === "error" && (
                    <AlertCircle className="h-4 w-4" />
                  )}
                  <span className="text-sm">{notification.message}</span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeNotification(notification.id)}
                  className="h-6 w-6 p-0"
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            </Alert>
          ))}
        </div>
      )}

      {/* QR Code Modal */}
      <QRCodeModal
        isOpen={showQRCode}
        onClose={() => setShowQRCode(false)}
        roomId={roomId}
      />
    </div>
  );
}
